import RefreshIcon from '@atlaskit/icon/core/refresh';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import VideoPlayIcon from '@atlaskit/icon/core/video-play';
import { token } from '@atlaskit/tokens';
import { DropdownItem, DropdownItemGroup } from '@databases/atlaskit/DropdownMenu';
import { type MenuItem } from '@databases/contextual-menu/ContextualMenu';
import { useFlags } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { DatabaseFieldType } from '@databases/types/database';
import { debounce, type DebouncedFunc } from 'lodash-es';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

export const AI_SUMMARY_RERUN_ENTIRE_COLUMN_ACTION = 'ai_summary_rerun_entire_column';
export const AI_SUMMARY_RERUN_EMPTY_ENTRIES_ACTION = 'ai_summary_rerun_empty_entries';

const AI_SUMMARY_RERUN_CONFIRMATION_FLAG_ID =
  'ai-summary-rerun-entire-column-confirmation';
const AI_SUMMARY_RERUN_CONFIRMATION_DEBOUNCE_MS = 8_000;

type ShowRerunConfirmation = (showConfirmation: VoidFunction) => void;

const rerunConfirmationDebouncers = new Map<
  string,
  DebouncedFunc<ShowRerunConfirmation>
>();

const getRerunConfirmationDebouncer = (confirmationKey: string) => {
  const existingDebouncer = rerunConfirmationDebouncers.get(confirmationKey);
  if (existingDebouncer) {
    return existingDebouncer;
  }

  const debouncer = debounce(
    (showConfirmation: VoidFunction) => showConfirmation(),
    AI_SUMMARY_RERUN_CONFIRMATION_DEBOUNCE_MS,
    { leading: true, trailing: false }
  );
  rerunConfirmationDebouncers.set(confirmationKey, debouncer);
  return debouncer;
};

const clearAllRerunConfirmationDebouncers = () => {
  for (const debouncer of rerunConfirmationDebouncers.values()) {
    debouncer.cancel();
  }
  rerunConfirmationDebouncers.clear();
};

type AiSummaryRunAction =
  | typeof AI_SUMMARY_RERUN_ENTIRE_COLUMN_ACTION
  | typeof AI_SUMMARY_RERUN_EMPTY_ENTRIES_ACTION;

type AiSummaryRunMenuItem = MenuItem<string> & {
  key: AiSummaryRunAction;
  testId: string;
};

type AiSummaryRunActions = {
  confirmationKey: string;
  hasUserEditedCells: () => boolean;
  onRerunEntireColumn?: () => void;
  onRerunEmptyEntries?: () => void;
  isDisabled?: boolean;
};

export const hasAiSummaryHeaderRefresh = (
  fieldType: DatabaseFieldType
): fieldType is DatabaseFieldType.AI_SUMMARY =>
  fieldType === DatabaseFieldType.AI_SUMMARY;

export const useAiSummaryRerunMenuItems = ({
  confirmationKey,
  hasUserEditedCells,
  onRerunEntireColumn,
  onRerunEmptyEntries,
  isDisabled = false
}: AiSummaryRunActions): AiSummaryRunMenuItem[] => {
  const { formatMessage } = useIntl();
  const { addFlag, clearFlags, hideFlag, showFlag } = useFlags();

  const showRerunConfirmation = useCallback(() => {
    if (!hasUserEditedCells()) {
      onRerunEntireColumn?.();
      return;
    }

    const debouncedShowConfirmation = getRerunConfirmationDebouncer(confirmationKey);
    debouncedShowConfirmation(() => {
      const flagId = `${AI_SUMMARY_RERUN_CONFIRMATION_FLAG_ID}:${confirmationKey}:${Date.now()}`;

      const clearRerunConfirmation = () => {
        debouncedShowConfirmation.cancel();
        if (
          rerunConfirmationDebouncers.get(confirmationKey) === debouncedShowConfirmation
        ) {
          rerunConfirmationDebouncers.delete(confirmationKey);
        }
      };

      const dismissRerunConfirmation = () => {
        clearRerunConfirmation();

        if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
          hideFlag(flagId);
        } else {
          clearAllRerunConfirmationDebouncers();
          clearFlags();
        }
      };

      const flag = {
        title: formatMessage(
          messages['field-actions.ai-summary-rerun-confirmation-title.non-final']
        ),
        description: formatMessage(
          messages['field-actions.ai-summary-rerun-confirmation-description.non-final']
        ),
        actions: [
          {
            content: formatMessage(
              messages['field-actions.ai-summary-rerun-confirmation-run.non-final']
            ),
            onClick: () => {
              dismissRerunConfirmation();
              onRerunEntireColumn?.();
            }
          },
          {
            content: formatMessage(
              messages['field-actions.ai-summary-rerun-confirmation-cancel.non-final']
            ),
            onClick: dismissRerunConfirmation
          }
        ]
      };

      if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
        showFlag({
          ...flag,
          id: flagId,
          type: 'warning',
          appearance: 'normal',
          close: 'auto',
          onClose: clearRerunConfirmation
        });
      } else {
        addFlag({
          ...flag,
          appearance: 'normal',
          onClose: clearRerunConfirmation,
          icon: () => (
            <StatusWarningIcon
              color={token('color.icon.warning', '#E2B203')}
              label=""
              spacing="spacious"
            />
          )
        });
      }
    });
  }, [
    addFlag,
    clearFlags,
    confirmationKey,
    formatMessage,
    hasUserEditedCells,
    hideFlag,
    onRerunEntireColumn,
    showFlag
  ]);

  return useMemo(
    () => [
      {
        key: AI_SUMMARY_RERUN_ENTIRE_COLUMN_ACTION,
        content: formatMessage(
          messages['field-actions.ai-summary-rerun-entire-column.non-final']
        ),
        elemBefore: <VideoPlayIcon label="" size="small" spacing="compact" />,
        onClick: showRerunConfirmation,
        isDisabled,
        testId: 'ai-summary-field-header-action-entire-column'
      },
      {
        key: AI_SUMMARY_RERUN_EMPTY_ENTRIES_ACTION,
        content: formatMessage(
          messages['field-actions.ai-summary-rerun-empty-entries.non-final']
        ),
        elemBefore: <RefreshIcon label="" size="small" spacing="compact" />,
        onClick: onRerunEmptyEntries,
        isDisabled,
        testId: 'ai-summary-field-header-action-empty-entries'
      }
    ],
    [formatMessage, isDisabled, onRerunEmptyEntries, showRerunConfirmation]
  );
};

export const AiSummaryRunDropdownItems = ({
  confirmationKey,
  hasUserEditedCells,
  onRerunEntireColumn,
  onRerunEmptyEntries,
  isDisabled
}: AiSummaryRunActions) => {
  const items = useAiSummaryRerunMenuItems({
    confirmationKey,
    hasUserEditedCells,
    onRerunEntireColumn,
    onRerunEmptyEntries,
    isDisabled
  });

  return (
    <DropdownItemGroup>
      {items.map((item) => (
        <DropdownItem
          elemBefore={item.elemBefore}
          isDisabled={item.isDisabled}
          key={item.key}
          onClick={item.onClick}
          testId={item.testId}
        >
          {item.content}
        </DropdownItem>
      ))}
    </DropdownItemGroup>
  );
};
